import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { useAutosave } from 'react-autosave';
import TextareaAutosize from 'react-textarea-autosize';
import { useAuth } from '../context/AuthContext';
import { Encrypt } from '../utils/encryption';
import {
  getHubPlanId,
  resolveUserSubscriptionAccess,
} from '../utils/subscriptionState';
import { resolveCatalogEntitlement } from '../services/subscriptionCatalog';
import { SUBSCRIPTION_LIMIT_KEYS } from '../utils/subscriptionCatalogSchema';
import { handleMedia } from '../config/api';
import '../styles/Notebook.css';

const Notebook = forwardRef(({
  userId,
  maxPages: propMaxPages,
  linesPerPage = 16,
  showHeader = true,
  autosaveInterval = 30000,
  readOnly = false,
}, ref) => {
  const {token, decodeToken, subscriptionCatalog} = useAuth();
  const [pages, setPages] = useState([{ content: 'Date: ' }]);
  const [goToPageInput, setGoToPageInput] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [wordCount, setWordCount] = useState(0);

  const maxEditableLines = linesPerPage - 1;
  const lastSerialized = useRef('');

  // Determine maxPages based on subscription tier
  const decodedToken = React.useMemo(() => {
    if (token) {
      return decodeToken(token);
    }
    return null;
  }, [token, decodeToken]);

  const subscriptionAccess = React.useMemo(
    () => resolveUserSubscriptionAccess(decodedToken),
    [decodedToken]
  );
  const selectedHubId = subscriptionAccess.hubProfile?.PK || '';
  const subscriptionTier = getHubPlanId(subscriptionAccess.hubProfile);
  const notebookEntitlement = React.useMemo(
    () => subscriptionCatalog
      ? resolveCatalogEntitlement(
          subscriptionCatalog,
          subscriptionTier,
          {
            moduleId: 'notebook',
            limitId: SUBSCRIPTION_LIMIT_KEYS.NOTEBOOK_PAGES,
          },
          subscriptionAccess.role
        )
      : null,
    [subscriptionAccess.role, subscriptionCatalog, subscriptionTier]
  );

  const maxPages = React.useMemo(() => {
    if (propMaxPages) return propMaxPages;
    if (notebookEntitlement?.allowed && Number.isFinite(notebookEntitlement.limit)) {
      return notebookEntitlement.limit;
    }
    return 10;
  }, [notebookEntitlement, propMaxPages]);

  // Check if current page is beyond the editable limit (readonly)
  const isCurrentPageReadOnly = React.useMemo(() => {
    if (readOnly) return true;
    return currentPage >= maxPages;
  }, [currentPage, maxPages, readOnly]);

  useEffect(() => {
    const fetchNotes = async () => {
      
      try {
        const response = await axios.post(handleMedia, Encrypt({
          action: 'fetchNotes',
          token,
          hubId: selectedHubId,
        })
        );

        const raw = response.data.notes || '';
        const parsed = raw
          .split('### NEW_PAGE:')
          .map((block) => block.trim())
          .filter(Boolean)
          .map((page) => ({ content: page }));

        setPages(parsed.length ? parsed : [{ content: 'Date: ' }]);

        // ✅ Set lastSerialized after successful fetch
        lastSerialized.current = parsed
          .map((p) => `### NEW_PAGE:\n${p.content.trim()}`)
          .join('\n');

      } catch (error) {
        console.error('Error fetching notes:', error);
      }
    };

    if (selectedHubId) fetchNotes();
  }, [userId, token, selectedHubId]);


  const handleGoToPage = () => {
    const pageNum = parseInt(goToPageInput, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= pages.length) {
      setCurrentPage(pageNum - 1);
    } else {
      alert(`Please enter a number between 1 and ${pages.length}`);
    }
    setGoToPageInput(''); // Clear input after attempt
  };

  const handleSaveNotes = async () => {
    if (!selectedHubId) return;

    const serialized = pages
      .map((p) => `### NEW_PAGE:\n${p.content.trim()}`)
      .join('\n');

    if (serialized === lastSerialized.current) return; // No change, no save

    try {
      await axios.post(handleMedia, Encrypt({ 
          action: 'saveNotes',
          notes: serialized ,
          token,
          hubId: selectedHubId,
        })
      );
      lastSerialized.current = serialized; // Update after successful save

    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  useAutosave({
    data: pages,
    onSave: handleSaveNotes,
    interval: autosaveInterval,
  });

  const current = pages[currentPage];
  const allLines = current.content.split('\n');
  const [firstLineRaw, ...rest] = allLines;
  const [, ...dateParts] = firstLineRaw.split(':');
  const editableDate = dateParts.join(':').trim();
  const editableText = rest.join('\n');

  useEffect(() => {
    const words = (editableDate + '\n' + editableText).match(/\b\w+\b/g) || [];
    setWordCount(words.length);
  }, [editableDate, editableText]);

  const updateEditableContent = (value) => {
    if (isCurrentPageReadOnly) return; // Prevent editing readonly pages

    const lines = value.split('\n');

    if (lines.length > maxEditableLines) {
      alert(`Only ${maxEditableLines} lines allowed per page (excluding "Date:").`);
      return;
    }

    const newFirstLine = `Date: ${editableDate}`;
    const newPageContent = [newFirstLine, ...lines].join('\n');

    setPages((prev) =>
      prev.map((p, i) => (i === currentPage ? { ...p, content: newPageContent } : p))
    );
  };

  const updateDateContent = (value) => {
    if (isCurrentPageReadOnly) return; // Prevent editing readonly pages

    const newFirstLine = `Date: ${value}`;
    const newPageContent = [newFirstLine, ...editableText.split('\n')].join('\n');

    setPages((prev) =>
      prev.map((p, i) => (i === currentPage ? { ...p, content: newPageContent } : p))
    );
  };

  const handlePrevPage = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < pages.length - 1) setCurrentPage(currentPage + 1);
  };

  const addNewPage = () => {
    if (pages.length >= maxPages) return;

    const today = new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

    const newPage = {
      content: `Date: ${today}`,
    };

    setPages([...pages, newPage]);
    setCurrentPage(pages.length);
  };
  
  const deleteCurrentPage = () => {
    if (pages.length <= 1) {
        alert("You must have at least one page.");
        return;
    }

    // Prevent deleting if current page is readonly (beyond limit)
    if (isCurrentPageReadOnly) {
      alert(`This page is read-only. You can only edit the first ${maxPages} pages with your current subscription.`);
      return;
    }

    const updatedPages = pages.filter((_, i) => i !== currentPage);
    const newCurrentPage = currentPage === 0 ? 0 : currentPage - 1;

    setPages(updatedPages);
    setCurrentPage(newCurrentPage);
  };

  useImperativeHandle(ref, () => ({
    handleSaveNotes
  }));

  return (
    <div className="notebook-wrapper">
      {showHeader && <h2>Take a Note</h2>}

      <div className="notebook-box">
        {isCurrentPageReadOnly && (
          <div style={{
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: '4px',
            padding: '8px 12px',
            marginBottom: '8px',
            fontSize: '0.9em',
            color: '#d46b08',
            textAlign: 'center'
          }}>
            📖 Read-only: This page is beyond your subscription limit. ({maxPages} editable pages with your current plan)
          </div>
        )}
        <div className="notebook-date-line">
          <strong>Date:</strong>{' '}
          <input
            type="text"
            value={editableDate}
            onChange={(e) => updateDateContent(e.target.value)}
            readOnly={readOnly || isCurrentPageReadOnly}
            style={{
              border: 'none',
              outline: 'none',
              fontFamily: 'monospace',
              fontWeight: 'normal',
              background: isCurrentPageReadOnly ? '#f5f5f5' : 'transparent',
              width: '70%',
              color: isCurrentPageReadOnly ? '#999' : 'inherit',
            }}
            title={isCurrentPageReadOnly ? `Read-only: Upgrade to edit beyond ${maxPages} pages` : ''}
          />
        </div>

        <TextareaAutosize
          value={editableText}
          onChange={(e) => updateEditableContent(e.target.value)}
          readOnly={readOnly || isCurrentPageReadOnly}
          minRows={maxEditableLines}
          maxRows={maxEditableLines}
          className="notebook-textarea-lined"
          style={{
            backgroundColor: isCurrentPageReadOnly ? '#f5f5f5' : undefined,
            color: isCurrentPageReadOnly ? '#999' : undefined,
          }}
          title={isCurrentPageReadOnly ? `Read-only: Upgrade to edit beyond ${maxPages} pages` : ''}
        />

        <div className="word-count">Word count: {wordCount}</div>
      </div>

      <div className="notebook-controls">
        <div className="controls-row">
          <div className="go-to-controls">
            <label htmlFor="goto">Go to:</label>
            <input
              id="goto"
              type="number"
              min="1"
              max={pages.length}
              value={goToPageInput}
              onChange={(e) => setGoToPageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleGoToPage();
              }}
            />
            <button onClick={handleGoToPage}>➡️</button>
          </div>

          <div className="nav-buttons">
            <button onClick={handlePrevPage} disabled={currentPage === 0}>◀</button>
            <span>
              {currentPage + 1} / {pages.length}
              {pages.length > maxPages && (
                <span style={{ fontSize: '0.85em', color: '#999', marginLeft: '4px' }}>
                  (Editable: {maxPages})
                </span>
              )}
            </span>
            <button onClick={handleNextPage} disabled={currentPage >= pages.length - 1}>▶</button>
          </div>

          {!readOnly && (
            <div className="action-controls">
              <button onClick={handleSaveNotes} title="Save">💾</button>
              {pages.length > 1 && !isCurrentPageReadOnly && (
                <button onClick={deleteCurrentPage} title="Remove Page">🗑 Page</button>
              )}
              {pages.length < maxPages && (
                <button onClick={addNewPage} title="Add Page">➕ Page</button>
              )}
              {pages.length >= maxPages && (
                <button 
                  disabled 
                  title={`Maximum ${maxPages} editable pages with your current subscription`}
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                >
                  ➕ Page (Max: {maxPages})
                </button>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
});

export default Notebook;
